import { useEffect, useState, type ReactNode } from "react";
import { buildShareIntents } from "@a2z/ui/share/intents";
import { Button } from "@/shared/components/Button";
import { IconButton, Modal } from "@a2z/ui";
import { copyTextToClipboard } from "@/lib/browser/clipboard";
import { useLocale, useTranslations, translateForLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { ShareMenuProps } from "@/shared/components/types/share";

const COPIED_RESET_MS = 1800;

/** The full ready-to-paste post = localized caption, then the link on its own line. */
function buildPost(caption: string, url: string): string {
  return `${caption}\n${url}`;
}

/**
 * Share a storefront link to social channels.
 *
 * ponytail: a ModalSheet (not a hand-rolled anchored dropdown like
 * ProductActionsMenu) — sharing is infrequent, a sheet works on every viewport,
 * is RTL-correct, and needs no portal/positioning math. Channels are the
 * web-intent links from `@a2z/ui/share/intents`; "Copy" opens an editable post
 * (with an EN/AR toggle) to paste anywhere; "More" surfaces the native share
 * sheet (the Instagram/any-app path on mobile) when available.
 */
export function ShareMenu({
  url,
  messageKey,
  messageValues,
  messageValuesEn,
  messageValuesAr,
  title,
  triggerLabel,
  triggerAriaLabel,
  triggerVariant = "outline",
  triggerClassName,
  open: controlledOpen,
  onClose: controlledOnClose,
  elevated,
}: ShareMenuProps) {
  const t = useTranslations();
  const locale = useLocale();
  const defaultLocale: "en" | "ar" = locale === "ar" ? "ar" : "en";
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const [view, setView] = useState<"main" | "compose">("main");
  const [composeLocale, setComposeLocale] = useState<"en" | "ar">(
    defaultLocale,
  );
  const [drafts, setDrafts] = useState<{ en: string; ar: string }>({
    en: "",
    ar: "",
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  // Resolve the caption in both languages — the compose view lets the seller
  // switch language and edit the post before copying. Per-locale values keep a
  // localized value (e.g. a product name) in its own language on each side.
  const captionEn = translateForLocale(
    "en",
    messageKey,
    messageValuesEn ?? messageValues,
  );
  const captionAr = translateForLocale(
    "ar",
    messageKey,
    messageValuesAr ?? messageValues,
  );
  const currentCaption = locale === "ar" ? captionAr : captionEn;

  // Channel intents auto-fill in the seller's current UI language.
  const intents = buildShareIntents({ url, text: currentCaption });
  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  const close = (): void => {
    if (isControlled) controlledOnClose?.();
    else setInternalOpen(false);
    setView("main");
    setCopied(false);
  };

  const openCompose = (): void => {
    // Seed both language drafts; switching language keeps each side's edits.
    setDrafts({ en: buildPost(captionEn, url), ar: buildPost(captionAr, url) });
    setComposeLocale(defaultLocale);
    setCopied(false);
    setView("compose");
  };

  const handleCopyDraft = async (): Promise<void> => {
    if (await copyTextToClipboard(drafts[composeLocale])) {
      setCopied(true);
    }
  };

  const handleNativeShare = async (): Promise<void> => {
    try {
      await navigator.share({ text: currentCaption, url });
      close();
    } catch {
      // User dismissed the native sheet (or the channel was cancelled) — there
      // is nothing to recover or report. ponytail: a dismissed share is not an error.
    }
  };

  return (
    <>
      {isControlled ? null : triggerLabel ? (
        <Button
          type="button"
          variant={triggerVariant}
          size="sm"
          leadingIcon={<ShareIcon />}
          onClick={() => setInternalOpen(true)}
          aria-label={triggerAriaLabel}
          className={triggerClassName}
        >
          {triggerLabel}
        </Button>
      ) : (
        <IconButton
          type="button"
          variant="outline"
          size="md"
          shape="square"
          icon={<ShareIcon />}
          ariaLabel={triggerAriaLabel}
          onClick={() => setInternalOpen(true)}
          className={triggerClassName}
        />
      )}

      <Modal
        isOpen={open}
        onClose={close}
        elevated={elevated}
        size="sm"
        icon={<ShareIcon />}
        iconTone="muted"
        title={view === "main" ? title : t(keys.share.composeTitle)}
        subtitle={view === "main" ? url : undefined}
        closeLabel={t(keys.common.close)}
        headerLeading={
          view === "compose" ? (
            <button
              type="button"
              onClick={() => setView("main")}
              aria-label={t(keys.share.back)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-secondary)] transition hover:bg-[color:var(--hover)]"
            >
              <BackIcon />
            </button>
          ) : undefined
        }
        hideFooter
      >
        {view === "main" ? (
          <div className="flex flex-col gap-2">
            <ShareChannelLink
              href={intents.whatsapp}
              label={t(keys.share.whatsapp)}
              icon={<WhatsAppIcon />}
              onNavigate={close}
            />
            <ShareChannelLink
              href={intents.x}
              label={t(keys.share.x)}
              icon={<XIcon />}
              onNavigate={close}
            />
            <ShareChannelLink
              href={intents.facebook}
              label={t(keys.share.facebook)}
              icon={<FacebookIcon />}
              onNavigate={close}
            />

            <ShareActionButton
              label={t(keys.share.composeAction)}
              icon={<CopyIcon />}
              onClick={openCompose}
            />

            {canNativeShare ? (
              <ShareActionButton
                label={t(keys.share.more)}
                icon={<MoreIcon />}
                onClick={handleNativeShare}
              />
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="inline-flex rounded-[10px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] p-0.5">
              <LangTab
                active={composeLocale === "en"}
                onClick={() => setComposeLocale("en")}
                label={t(keys.common.languageEnglish)}
              />
              <LangTab
                active={composeLocale === "ar"}
                onClick={() => setComposeLocale("ar")}
                label={t(keys.common.languageArabic)}
              />
            </div>

            <textarea
              value={drafts[composeLocale]}
              onChange={(event) =>
                setDrafts((prev) => ({
                  ...prev,
                  [composeLocale]: event.target.value,
                }))
              }
              // Stop keys (esp. Enter) from bubbling to a clickable parent card
              // (e.g. the product grid card's role="link" keydown handler).
              onKeyDown={(event) => event.stopPropagation()}
              dir={composeLocale === "ar" ? "rtl" : "ltr"}
              rows={5}
              className="w-full resize-none rounded-[12px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] p-3 text-[15px] leading-relaxed text-[color:var(--text-primary)] outline-none transition focus:border-[color:var(--border-strong)]"
            />

            <Button
              type="button"
              variant="primary"
              size="sm"
              leadingIcon={copied ? <CheckIcon /> : <CopyIcon />}
              onClick={handleCopyDraft}
              className="w-full"
            >
              {copied ? t(keys.share.copied) : t(keys.share.copy)}
            </Button>
          </div>
        )}
      </Modal>
    </>
  );
}

function ShareChannelLink({
  href,
  label,
  icon,
  onNavigate,
}: {
  href: string;
  label: string;
  icon: ReactNode;
  onNavigate: () => void;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onNavigate}
      className="flex items-center gap-3 rounded-[12px] border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 text-[15px] font-semibold text-[color:var(--text-primary)] transition hover:border-[color:var(--border-strong)] hover:bg-[color:var(--surface-alt)]"
    >
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center text-[color:var(--text-secondary)]">
        {icon}
      </span>
      {label}
    </a>
  );
}

function ShareActionButton({
  label,
  icon,
  onClick,
  highlighted = false,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  highlighted?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-3 rounded-[12px] border px-4 py-3 text-start text-[15px] font-semibold transition ${
        highlighted
          ? "border-[color:var(--success-500)] bg-[color:var(--success-100)] text-[color:var(--success-500)]"
          : "border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:border-[color:var(--border-strong)] hover:bg-[color:var(--surface-alt)]"
      }`}
    >
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center">
        {icon}
      </span>
      {label}
    </button>
  );
}

function LangTab({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-[8px] px-3 py-1.5 text-[13px] font-semibold transition ${
        active
          ? "bg-[color:var(--surface)] text-[color:var(--text-primary)] shadow-[var(--shadow-sm)]"
          : "text-[color:var(--text-muted)]"
      }`}
    >
      {label}
    </button>
  );
}

// --- Icons (inlined — seller app has no icon library) ---------------------

function ShareIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
      <path d="M12.04 2c-5.46 0-9.9 4.44-9.9 9.9 0 1.74.46 3.45 1.32 4.95L2 22l5.27-1.38a9.9 9.9 0 0 0 4.77 1.22c5.46 0 9.9-4.44 9.9-9.9S17.5 2 12.04 2zm0 18.05c-1.5 0-2.97-.4-4.25-1.16l-.3-.18-3.13.82.84-3.05-.2-.31a8.2 8.2 0 0 1-1.26-4.36c0-4.54 3.7-8.23 8.25-8.23 4.54 0 8.23 3.69 8.23 8.23 0 4.54-3.69 8.24-8.23 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.8-.79.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.42.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.42l-.48-.01c-.16 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.16 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
      <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11.01 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.08 24 18.09 24 12.07z" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-[18px] w-[18px] text-[color:var(--text-secondary)]"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-[18px] w-[18px]"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-[18px] w-[18px] text-[color:var(--text-secondary)]"
      fill="currentColor"
    >
      <circle cx="5" cy="12" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="19" cy="12" r="1.8" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-5 w-5 rtl:rotate-180"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}
